/**
 * Связи элемента.
 *
 * Два списка: исходящие связи и обратные ссылки. Подпись зависит от
 * направления — «подтверждает» против «подтверждается», поэтому одна и та же
 * связь читается осмысленно с обеих сторон.
 *
 * Интерактивный граф в первой версии не нужен (документ «Итоговое описание»,
 * §17): списка и обратных ссылок достаточно, чтобы пройти от вывода к его
 * основанию.
 */

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Link2Icon,
  Loader2Icon,
  PlusIcon,
  XIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  RELATION_LABELS_RU,
  RELATION_TYPES,
  type ItemRelationDto,
  type RelationType,
} from '@project-os/contracts';
import { ApiError } from '@/api/client';
import {
  useCreateRelation,
  useDeleteRelation,
  useItemRelations,
  useRelationTargets,
} from '@/api/collaboration';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { EmptyState, ErrorState } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ItemTypeIcon } from './item-icon';

export function RelationsSection({
  projectId,
  itemId,
  canEdit,
  onOpenItem,
}: {
  projectId: string;
  itemId: string;
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
}) {
  const { data, isPending, isError, error, refetch } = useItemRelations(projectId, itemId);
  const [adding, setAdding] = useState(false);

  if (isPending) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const total = data.outgoing.length + data.incoming.length;

  return (
    <div className="space-y-4">
      {total === 0 && !adding ? (
        <EmptyState
          icon={<Link2Icon />}
          title="Связей пока нет"
          description="Связи показывают, на чём основан вывод и что из него следует: источник подтверждает метрику, исследование отвечает на вопрос, задача создаёт результат."
          hint="Связь двусторонняя: она появится и в карточке второго элемента — искать её там вручную не придётся."
          action={
            canEdit ? (
              <Button size="sm" onClick={() => setAdding(true)}>
                <PlusIcon />
                Добавить связь
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          {data.outgoing.length > 0 ? (
            <RelationList
              title="Исходящие связи"
              icon={<ArrowRightIcon className="size-3.5" />}
              relations={data.outgoing}
              projectId={projectId}
              itemId={itemId}
              canEdit={canEdit}
              onOpenItem={onOpenItem}
            />
          ) : null}

          {data.incoming.length > 0 ? (
            <RelationList
              title="Обратные ссылки"
              icon={<ArrowLeftIcon className="size-3.5" />}
              relations={data.incoming}
              projectId={projectId}
              itemId={itemId}
              // Обратную ссылку удаляет владелец исходной связи, а не эта карточка.
              canEdit={false}
              onOpenItem={onOpenItem}
            />
          ) : null}

          {canEdit && !adding ? (
            <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
              <PlusIcon />
              Добавить связь
            </Button>
          ) : null}
        </>
      )}

      {adding ? (
        <AddRelationForm projectId={projectId} itemId={itemId} onDone={() => setAdding(false)} />
      ) : null}
    </div>
  );
}

function RelationList({
  title,
  icon,
  relations,
  projectId,
  itemId,
  canEdit,
  onOpenItem,
}: {
  title: string;
  icon: React.ReactNode;
  relations: ItemRelationDto[];
  projectId: string;
  itemId: string;
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
}) {
  const remove = useDeleteRelation(projectId, itemId);

  const handleRemove = async (relationId: string) => {
    try {
      await remove.mutateAsync(relationId);
      toast.success('Связь удалена');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось удалить связь');
    }
  };

  return (
    <section>
      <h4 className="mb-2 flex items-center gap-1.5 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
        {icon}
        {title}
      </h4>
      <ul className="divide-y rounded-md border">
        {relations.map((relation) => (
          <li key={relation.id} className="flex items-center gap-2 px-3 py-2">
            <span className="w-28 shrink-0 text-xs text-muted-foreground">{relation.labelRu}</span>
            <button
              type="button"
              onClick={() => onOpenItem(relation.item.id)}
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
            >
              <ItemTypeIcon type={relation.item.type} className="size-3.5" />
              <span className="truncate text-sm hover:underline">{relation.item.title}</span>
            </button>
            <ItemStatusBadge type={relation.item.type} status={relation.item.status} />
            {canEdit ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-6 shrink-0 text-muted-foreground hover:text-destructive"
                disabled={remove.isPending}
                onClick={() => void handleRemove(relation.id)}
                aria-label="Удалить связь"
              >
                <XIcon className="size-3.5" />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function AddRelationForm({
  projectId,
  itemId,
  onDone,
}: {
  projectId: string;
  itemId: string;
  onDone: () => void;
}) {
  const [relationType, setRelationType] = useState<RelationType>('RELATED_TO');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const create = useCreateRelation(projectId, itemId);
  const targets = useRelationTargets(projectId, itemId, relationType, debounced, pickerOpen);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSelect = async (toItemId: string) => {
    try {
      await create.mutateAsync({ toItemId, relationType });
      toast.success('Связь создана');
      setPickerOpen(false);
      onDone();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось создать связь');
    }
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="space-y-1.5">
        <Label htmlFor="relation-type">Тип связи</Label>
        <Select
          value={relationType}
          onValueChange={(value) => setRelationType(value as RelationType)}
        >
          <SelectTrigger id="relation-type" size="sm" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RELATION_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                Этот элемент {RELATION_LABELS_RU[type].forward} …
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>Элемент</Label>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-start font-normal">
              {create.isPending ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                <PlusIcon className="text-muted-foreground" />
              )}
              Выбрать элемент проекта
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Поиск по названию…"
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                {targets.isPending ? (
                  <div className="p-3 text-xs text-muted-foreground">Загрузка…</div>
                ) : (
                  <>
                    <CommandEmpty>Подходящих элементов не найдено</CommandEmpty>
                    <CommandGroup>
                      {(targets.data ?? []).map((target) => (
                        <CommandItem
                          key={target.id}
                          value={target.id}
                          onSelect={() => void handleSelect(target.id)}
                          className="gap-2"
                        >
                          <ItemTypeIcon type={target.type} className="size-3.5" />
                          <span className="min-w-0 flex-1 truncate">{target.title}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </div>
  );
}
